import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@emotion/react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { brainStyles } from "../../../theme/style";
import TagInput from "./TagInput";
import APP_CONSTANTS from "../../../utils/config";

export default function ConflictDialog({
  open,
  onClose,
  conflicts,
  // onRefresh,
  columns,
}) {
  const theme = useTheme();
  const classes = brainStyles(theme);
  const renderCell = (row, colKey, curr_column) => {
    const cell = row[colKey];
    const isString = (value) => typeof value === "string";

    if (isString(cell)) {
      return (
        <TableCell
          className={classes.baseCell}
          sx={{
            fontSize: "0.7rem",
            color: theme.palette.brain.table.row.text,
          }}
        >
          {cell}
        </TableCell>
      );
    }
    if (!cell) {
      return (
        <TableCell key={colKey} className={classes.baseCell}>
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            —
          </Typography>
        </TableCell>
      );
    }

    const cellType = curr_column?.type;
    const cellStyle = {
      backgroundColor: theme.palette.brain.grey.background,
      borderLeft: `3px solid ${theme.palette.brain.grey.border}`,
      transition: "all 0.15s ease",
    };
    if (cellType === "tag") {
      const value = cell.value;
      const tagValues = Array.isArray(value)
        ? value
        : value
        ? [String(value)]
        : [];

      return (
        <TableCell key={colKey} className={classes.baseCell} sx={cellStyle}>
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <TagInput
              value={tagValues}
              disabled={true}
              onChange={() => {}}
              maxVisible={2}
            />
          </Box>
        </TableCell>
      );
    }

    const formatCellValue = (cell) => {
      if (!cell) return "—";
      return cell.value || "—";
    };

    return (
      <TableCell key={colKey} className={classes.baseCell}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.78rem",
              color: theme.palette.brain.table.row.text,
            }}
          >
            {formatCellValue(cell)}
          </Typography>
        </Box>
      </TableCell>
    );
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          background: theme.palette.brain.table.tableBackground,
        },
      }}
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: theme.palette.brain.error.main,
        }}
      >
        <WarningAmberIcon />
        Submission Conflicts
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ mb: 2, color: theme.palette.brain.table.row.text }}
        >
          Some rows could not be saved due to conflicts with other users'
          changes.
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
            backgroundColor: theme.palette.brain.table.tableBackground,
            borderRadius: 2,
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.brain.mis.grey.background1,
              borderRadius: 3,
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={classes.tableHeader}
                    sx={{
                      minWidth: ["upc", "sku"].includes(col.key)
                        ? 100
                        : col.key === "description"
                        ? 300
                        : 200,

                      minWidth: APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(
                        col.key
                      )
                        ? 50
                        : col.key === "description"
                        ? 300
                        : 200,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>{col.label}</span>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {conflicts.map((row, idx) => (
                <TableRow
                  sx={{
                    backgroundColor:
                      idx % 2 === 0
                        ? "transparent"
                        : theme.palette.brain.table.row.background,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.brain.table.row.hoverBackground,
                    },
                    transition: "background-color 0.12s ease",
                  }}
                >
                  {columns.map((col) => renderCell(row, col.key, col))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: theme.palette.secondary.main2,
            color: theme.palette.secondary.main2,
            "&:hover": {
              borderColor: theme.palette.secondary.main2,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
